import {
  Box,
  Stack,
  Typography,
  Button as MuiButton,
  CircularProgress,
  Modal,
} from '@mui/material';
import {styled} from '@mui/material/styles';
import React, {FC, useCallback, useRef, useState} from 'react';
import {FileRejection, useDropzone} from 'react-dropzone';
import {BaseBox} from './BaseBox';
import {Button} from '../components/Button';
import {IconButton} from '../components/IconButton';
import {ArrowRightIcon} from '../images/ArrowRightIcon';
import {DeleteIcon} from '../images/DeleteIcon';
import {ZoomIcon} from '../images/ZoomIcon';

const MAX_FILE_SIZE = 50 * 1024 * 1024;

export type UploadFile = {
  name: string | null;
  size: number;
  data: File;
  error: boolean;
};
type Props = {
  files: UploadFile[];
  onChangeFiles: (file: UploadFile[]) => void;
  onAnalyze: () => void;
  isAnalyzing: boolean;
};

export const PropertyFileUploader: FC<Props> = ({
  files,
  onChangeFiles,
  onAnalyze,
  isAnalyzing,
}) => {
  const [previewIndex, setPreviewIndex] = useState<number>(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const ref = useRef<HTMLIFrameElement | null>(null);
  const accept = ['image/jpeg', 'image/png', 'application/pdf'];
  const onDrop = (acceptedFiles: File[]) => {
    if (
      files.some(uploadFile => {
        return acceptedFiles.some((acceptedFile: File) => {
          return uploadFile.name === acceptedFile.name;
        });
      })
    ) {
      return;
    }
    const draftFiles: UploadFile[] = [...files];
    acceptedFiles.forEach((file: File) => {
      draftFiles.push({
        name: file.name,
        size: file.size,
        data: file,
        error: false,
      });
    });
    onChangeFiles(draftFiles);
  };
  const onDropRejected = useCallback((rejectedFiles: FileRejection[]) => {
    rejectedFiles.forEach(({file, errors}) => {
      errors.forEach(({code}) => {
        let message = 'エラーが発生しました。';
        switch (code) {
          case 'file-too-large':
            message = `${file.name} のファイルサイズが大きすぎます。50MB以下のファイルをアップロードしてください。`;
            break;
          case 'file-invalid-type':
            message = `${file.name} は対応していないファイル形式です。対応可能なファイル形式は jpg, png, pdfです。`;
            break;
          default:
            break;
        }
        alert(message);
      });
    });
  }, []);
  const {getRootProps, getInputProps} = useDropzone({
    onDrop,
    onDropRejected,
    accept,
    maxSize: MAX_FILE_SIZE,
  });
  const onDeleteFile = useCallback(() => {
    const draftUploadFiles = [...files];
    draftUploadFiles.splice(previewIndex, 1);
    onChangeFiles(draftUploadFiles);
    if (previewIndex !== 0 && files.length > 0) {
      setPreviewIndex(prev => prev - 1);
    }
  }, [onChangeFiles, previewIndex, files]);

  return (
    <>
      <Box
        {...getRootProps()}
        sx={{
          border: '3px dashed #A1A5B6',
          bgcolor: 'flikGray.-30',
          boxShadow: 'none',
          p: 5,
          minHeight: 400,
          maxHeight: 'calc(100vh - 200px)',
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          flexShrink: 2,
          minWidth: 450,
          position: 'sticky',
          top: '112px',
        }}>
        <input {...getInputProps()} />
        <Stack
          direction="column"
          justifyContent="center"
          alignItems="center"
          alignSelf="center"
          spacing={1.5}>
          {files.length > 0 ? (
            <Stack
              mb={1}
              spacing={1}
              alignItems="center"
              onClick={e => e.stopPropagation()}>
              <Stack spacing={1}>
                {files[previewIndex].data.type === 'application/pdf' ? (
                  <iframe
                    ref={ref}
                    src={`${URL.createObjectURL(files[previewIndex].data)}`}
                    height={300}
                    width={400}
                    onClick={() => setIsZoomed(true)}
                  />
                ) : (
                  <img
                    src={`${URL.createObjectURL(files[previewIndex].data)}`}
                    style={{
                      width: '100%',
                      height: 300,
                      objectFit: 'contain',
                      cursor: 'zoom-in',
                    }}
                    onClick={() => setIsZoomed(true)}
                  />
                )}
                <button
                  style={{
                    cursor: 'pointer',
                    width: 'fit-content',
                    alignSelf: 'center',
                  }}
                  onClick={() => setIsZoomed(true)}>
                  <Stack
                    flexDirection="row"
                    margin="auto"
                    alignItems="center"
                    justifyContent="center">
                    <ZoomIcon
                      sx={{
                        fill: 'transparent !important',
                        fontSize: '16px !important',
                      }}
                    />
                    <Typography fontSize={12}>拡大</Typography>
                  </Stack>
                </button>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography fontSize={12}>
                    {files[previewIndex].name}
                  </Typography>
                  <IconButton
                    sx={{
                      width: 24,
                      height: 24,
                      bgcolor: 'flikGray.-25',
                      '& svg': {
                        width: 16,
                        height: 16,
                      },
                    }}
                    tooltip="削除"
                    onClick={onDeleteFile}>
                    <DeleteIcon />
                  </IconButton>
                </Stack>
              </Stack>
              <Stack direction="row" alignItems="center" spacing={1}>
                <StyledMuiButton
                  sx={{padding: '6px'}}
                  disabled={previewIndex === 0}
                  onClick={() =>
                    previewIndex > 0 && setPreviewIndex(prev => prev - 1)
                  }>
                  <ArrowRightIcon
                    sx={{
                      fill: 'transparent !important',
                      fontSize: '16px !important',
                      transform: 'rotate(180deg)',
                      stroke: previewIndex === 0 ? '#BEC0CC' : 'black',
                    }}
                  />
                </StyledMuiButton>
                <Typography fontSize={14}>
                  {previewIndex + 1} / {files.length}
                </Typography>
                <StyledMuiButton
                  sx={{padding: '6px'}}
                  disabled={previewIndex === files.length - 1}
                  onClick={() =>
                    previewIndex !== files.length - 1 &&
                    setPreviewIndex(prev => prev + 1)
                  }>
                  <ArrowRightIcon
                    sx={{
                      fill: 'transparent !important',
                      stroke:
                        previewIndex === files.length - 1 ? '#BEC0CC' : 'black',
                      fontSize: '16px !important',
                    }}
                  />
                </StyledMuiButton>
              </Stack>
            </Stack>
          ) : (
            <>
              <Typography
                fontSize={16}
                fontWeight={500}
                lineHeight={1.8}
                textAlign="center">
                アップロードするファイルを
                <br />
                ここにドラッグ&ドロップ
              </Typography>
              <Typography fontSize={14}>または</Typography>
            </>
          )}
          <Stack direction="row" spacing={2.5}>
            <Button variant="outlined">ファイルを選択</Button>
            {files.length > 0 && (
              <Button
                variant="contained"
                onClick={e => {
                  onAnalyze();
                  e.stopPropagation();
                }}
                disabled={isAnalyzing}>
                {isAnalyzing ? (
                  <CircularProgress
                    sx={{
                      color: 'white',
                      width: '20px  !important',
                      height: '20px  !important',
                    }}
                  />
                ) : (
                  ' ファイルを解析する'
                )}
              </Button>
            )}
          </Stack>
          <Typography fontSize={12} fontWeight={300}>
            ※対応ファイル形式：pdf / png / jpg
          </Typography>
        </Stack>
      </Box>
      {isZoomed && (
        <Modal open={isZoomed} onClose={() => setIsZoomed(false)}>
          <Stack
            sx={{
              position: 'absolute' as const,
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 'calc(100% - 160px)',
              height: 'calc(100% - 160px)',
              boxShadow: 'none',
              justifyContent: 'center',
            }}
            onClick={() => setIsZoomed(false)}>
            {files[previewIndex].data.type === 'application/pdf' ? (
              <iframe
                ref={ref}
                src={`${URL.createObjectURL(files[previewIndex].data)}`}
                height="100%"
                onClick={() => setIsZoomed(true)}
              />
            ) : (
              <img
                src={`${URL.createObjectURL(files[previewIndex].data)}`}
                style={{
                  height: '100%',
                  width: '100%',
                  objectFit: 'contain',
                }}
              />
            )}
          </Stack>
        </Modal>
      )}
      <LoadingModal open={isAnalyzing} />
    </>
  );
};

const StyledMuiButton = styled(MuiButton)(({theme}) => ({
  backgroundColor: theme.palette.flikGray[-25],
  color: theme.palette.common.black,
  borderRadius: '6px',
  padding: '5px 8px',
  minWidth: 'unset',
  '&:hover': {
    backgroundColor: theme.palette.flikPrimary.main,
    color: 'white',
  },
  '&:hover svg': {
    stroke: 'white',
    transition: 'stroke .25s',
  },
  '&.Mui-disabled': {
    backgroundColor: '#F5F8FA',
  },
}));

const LoadingModal: FC<{open: boolean}> = ({open}) => {
  return (
    <Modal open={open}>
      <BaseBox
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          maxWidth: 350,
          boxShadow: 'none',
        }}>
        <Stack justifyContent="center" alignItems="center" spacing={1.5}>
          <CircularProgress />
          <Typography align="center" fontWeight={600} fontSize={18}>
            ファイル解析中
          </Typography>
          <Typography fontSize={14}>
            ファイルを解析しています。画面を閉じずにそのままお待ち下さい。
          </Typography>
        </Stack>
      </BaseBox>
    </Modal>
  );
};
